import React, { useState, useEffect } from "react";
import {
  AppBar, Toolbar, Box, Button, IconButton, Drawer,
  List, ListItem, ListItemButton, ListItemText,
  useTheme, useMediaQuery, Container, Typography, styled,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import LanguageSwitcher from "../components/common/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";

const BLUE      = "#1565C0";
const NAVY      = "#0A1628";
const WHITE     = "#FFFFFF";
const BORDER    = "rgba(21,101,192,0.1)";
const TEXT_DARK = "#0A1628";

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (p) => p !== "scrolled",
})<{ scrolled: boolean }>(({ scrolled }) => ({
  backgroundColor: WHITE,
  boxShadow: scrolled ? "0 2px 24px rgba(21,101,192,0.1)" : "none",
  borderBottom: scrolled ? "none" : `1px solid ${BORDER}`,
  transition: "all 0.3s ease",
}));

const NavLink = styled(Button)({
  color: TEXT_DARK,
  fontWeight: 500,
  fontSize: "0.92rem",
  textTransform: "none",
  padding: "8px 16px",
  borderRadius: "10px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(21,101,192,0.06)",
    color: BLUE,
  },
});

const BookButton = styled(Button)({
  backgroundColor: BLUE,
  color: WHITE,
  borderRadius: "50px",
  padding: "10px 28px",
  fontSize: "0.9rem",
  fontWeight: 700,
  textTransform: "none",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 20px rgba(21,101,192,0.3)",
  "&:hover": {
    backgroundColor: NAVY,
    transform: "translateY(-2px)",
    boxShadow: "0 8px 28px rgba(21,101,192,0.4)",
    color: WHITE,
  },
});

const Navbar: React.FC = () => {
  const [scrolled,    setScrolled]    = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const theme      = useTheme();
  const isMobile   = useMediaQuery(theme.breakpoints.down("md"));
  const navigate   = useNavigate();
  const location   = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const tn = t.nav;

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const menuItems = [
    { label: tn.home,    path: "/"         },
    { label: tn.classes, hash: "pricing"   },
    { label: tn.pricing, hash: "pricing"   },
    { label: tn.about,   hash: "about"     },
    { label: tn.contact, hash: "contact"   },
  ];

  const handleNavClick = (item: { path?: string; hash?: string }) => {
    if (item.hash) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(item.hash!)?.scrollIntoView({ behavior: "smooth" });
        }, 200);
      } else {
        document.getElementById(item.hash!)?.scrollIntoView({ behavior: "smooth" });
      }
    } else if (item.path) {
      navigate(item.path);
    }
    setMobileOpen(false);
  };

  const handlePrimaryAction = () => {
    navigate(isAuthenticated ? "/membership/classes" : "/login");
    setMobileOpen(false);
  };

  return (
    <>
      <StyledAppBar position="fixed" scrolled={scrolled} elevation={0}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: "space-between", height: "80px", gap: 2 }}>

            {/* ── Logo ─────────────────────────────────────────── */}
            <Box onClick={() => navigate("/")} sx={{ cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center" }}>
              <Box component="img" src="/logo.png" alt="Dr.Paw" sx={{ height: { xs: 48, md: 56 }, width: "auto", objectFit: "contain" }} />
            </Box>

            {/* ── Desktop nav links ─────────────────────────────── */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {menuItems.map((item) => (
                  <NavLink
                    key={item.label}
                    {...({ component: "a", href: item.path ?? `/#${item.hash}` } as any)}
                    onClick={(e: React.MouseEvent) => { e.preventDefault(); handleNavClick(item); }}
                  >
                    {item.label}
                  </NavLink>
                ))}
                {isAuthenticated && (
                  <NavLink
                    {...({ component: "a", href: "/membership/classes" } as any)}
                    onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate("/membership/classes"); }}
                  >
                    {tn.myAccount}
                  </NavLink>
                )}
              </Box>
            )}

            {/* ── Right actions ─────────────────────────────────── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 2 }, flexShrink: 0 }}>
              <LanguageSwitcher variant="dark" />

              {!isMobile && (
                <>
                  {/* Phone */}
                  <Box component="a" href="tel:+9614541965" sx={{ display: "flex", alignItems: "center", gap: 1, textDecoration: "none", mr: 1, transition: "opacity 0.2s ease", "&:hover": { opacity: 0.75 } }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: "10px", backgroundColor: "rgba(21,101,192,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <PhoneIcon sx={{ color: BLUE, fontSize: "1rem" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: NAVY, lineHeight: 1.2 }}>
                        04 541 965
                      </Typography>
                      <Typography sx={{ fontSize: "0.68rem", color: "#78909C" }}>
                        {tn.emergencyLine}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Profile icon */}
                  <IconButton onClick={() => navigate(isAuthenticated ? "/membership/classes" : "/login")} sx={{ color: BLUE, border: `1px solid ${BORDER}`, borderRadius: "10px", p: 1 }}>
                    <PersonOutlineIcon fontSize="small" />
                  </IconButton>

                  {/* CTA */}
                  <BookButton onClick={handlePrimaryAction} endIcon={<ArrowForwardIcon sx={{ transform: isRTL ? "scaleX(-1)" : "none", fontSize: "1rem !important" }} />}>
                    {tn.startLearning}
                  </BookButton>
                </>
              )}

              {/* Mobile hamburger */}
              {isMobile && (
                <IconButton onClick={() => setMobileOpen(true)} sx={{ color: NAVY, border: `1px solid ${BORDER}`, borderRadius: "10px" }}>
                  <MenuIcon />
                </IconButton>
              )}
            </Box>

          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* ── Mobile Drawer ─────────────────────────────────────────── */}
      <Drawer
        anchor={isRTL ? "left" : "right"}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        transitionDuration={350}
        PaperProps={{
          sx: { width: { xs: "100%", sm: "360px" }, backgroundColor: WHITE, display: "flex", flexDirection: "column" },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${BORDER}` }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box component="img" src="/logo.png" alt="Dr.Paw" sx={{ height: 44, width: "auto", objectFit: "contain" }} />
          </Box>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px" }}>
            <CloseIcon sx={{ color: NAVY }} />
          </IconButton>
        </Box>

        {/* Nav items */}
        <List sx={{ px: 3, py: 4, flexGrow: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
              <ListItemButton onClick={() => handleNavClick(item)} sx={{ borderRadius: "12px", py: 1.8, border: `1px solid ${BORDER}`, backgroundColor: "rgba(21,101,192,0.04)", "&:hover": { backgroundColor: "rgba(21,101,192,0.08)" } }}>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: "1.05rem", color: TEXT_DARK }}
                />
                <ArrowForwardIcon sx={{ color: BLUE, fontSize: "1rem", transform: isRTL ? "scaleX(-1)" : "none" }} />
              </ListItemButton>
            </ListItem>
          ))}
          {isAuthenticated && (
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton onClick={() => { navigate("/membership/classes"); setMobileOpen(false); }} sx={{ borderRadius: "12px", py: 1.8, "&:hover": { backgroundColor: "rgba(21,101,192,0.06)" } }}>
                <ListItemText primary={tn.myAccount} primaryTypographyProps={{ fontWeight: 600, fontSize: "1.05rem", color: TEXT_DARK }} />
                <ArrowForwardIcon sx={{ color: BLUE, fontSize: "1rem", transform: isRTL ? "scaleX(-1)" : "none" }} />
              </ListItemButton>
            </ListItem>
          )}
          {isAuthenticated && (
            <ListItem disablePadding>
              <ListItemButton onClick={async () => { await logout(); navigate("/"); setMobileOpen(false); }} sx={{ borderRadius: "12px", py: 1.8, "&:hover": { backgroundColor: "rgba(21,101,192,0.06)" } }}>
                <ListItemText primary={tn.logout} primaryTypographyProps={{ fontWeight: 600, fontSize: "1.05rem", color: TEXT_DARK }} />
              </ListItemButton>
            </ListItem>
          )}
        </List>

        {/* Footer */}
        <Box sx={{ p: 3, borderTop: `1px solid ${BORDER}` }}>
          <BookButton fullWidth onClick={handlePrimaryAction} endIcon={<ArrowForwardIcon sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />} sx={{ py: 2, fontSize: "1rem", mb: 3 }}>
            {tn.startLearning}
          </BookButton>
          <Box component="a" href="tel:+9614541965" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, textDecoration: "none", p: 2, borderRadius: "12px", border: `1px solid ${BORDER}`, transition: "all 0.2s ease", "&:hover": { backgroundColor: "rgba(21,101,192,0.04)" } }}>
            <PhoneIcon sx={{ color: BLUE, fontSize: "1.1rem" }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: NAVY }}>
                04 541 965
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "#78909C" }}>
                {tn.emergencyLine24}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
